import React, {useEffect, useState, useRef} from 'react'
import Header from "../components/Header"
import BreadCrumbs from "../components/BreadCrumbs"
import {apiOffers, apiMy, apiSubsAdd} from "api-methods"
import {Loaders} from "../components/Loaders"
import Notification from "../components/Notification"
import Footer from "../components/Footer"
import '../css/SubscriptionAdd.css'
import CustomSelect from "../components/CustomSelect"
import {useLocation, useNavigate} from "react-router-dom";
import { handleAuthErrorAndRedirect, isSuccessStatus } from '../utils/authError'

const SubscriptionAdd = () => {
    const MIN_LOADER_MS = 350
    const navigate = useNavigate()
    const location = useLocation()

    function validateEmail(email) {const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; return regex.test(email)}

    const [notification, setNotification] = useState(null)
    const showNotification = (message, type = 'success') => {setNotification({ message, type })}
    const hideNotification = () => {setNotification(null)}

    const [loader, setLoader] = useState(true)
    const [productList, setProductList] = useState([])
    const [currentStep, setCurrentStep] = useState(1)
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [selectedQuantity, setSelectedQuantity] = useState(1)
    const [userData, setUserData] = useState(null)
    const [editedUserData, setEditedUserData] = useState({})
    const [isLdap, setIsLdap] = useState(false)
    const [profileLoaded, setProfileLoaded] = useState(false)
    const [approvalCode, setApprovalCode] = useState('')
    const [specialPrice, setSpecialPrice] = useState('')
    const [subscriptionSuccess, setSubscriptionSuccess] = useState(false) // Новое состояние
    const [subscribing, setSubscribing] = useState(false)

    // Новые состояния для управления конкретным продуктом
    const [currentProduct, setCurrentProduct] = useState(null)
    const [globalMinQuantity, setGlobalMinQuantity] = useState(1)
    const [globalMaxQuantity, setGlobalMaxQuantity] = useState(1)

    const intervalRef = useRef(null)
    const accelerateIntervalRef = useRef(null)
    const delayRef = useRef(300)

    useEffect(() => {
        const loadStartedAt = Date.now()
        apiMy()
            .then(data => {
                if (handleAuthErrorAndRedirect({statusCode: data.code, navigate, location})) return
                if (isSuccessStatus(data.code)) {
                    const profile = data.data ?? {}
                    setIsLdap(Object.prototype.hasOwnProperty.call(profile, 'isLdap') && profile.isLdap === true)
                }
            })
            .catch(_ => {})
            .finally(() => setProfileLoaded(true))

        apiOffers(false)
            .then(data => {
                if (handleAuthErrorAndRedirect({statusCode: data.code, navigate, location})) return
                if (isSuccessStatus(data.code)) {
                    if (typeof data.data === 'string') {data.data = JSON.parse(data.data)}
                    data.data = data.data.sort((a, b) => a.name.localeCompare(b.name))

                    const processedData = data.data.map(item => {
                        if (item.products && Array.isArray(item.products)) {
                            let minQuantity = Infinity
                            let maxQuantity = -Infinity
                            item.products.forEach(product => {
                                if (product?.optionsSchema?.properties?.quantity?.minimum !== undefined) {
                                    const currentMin = product.optionsSchema.properties.quantity.minimum
                                    if (currentMin < minQuantity) {minQuantity = currentMin}
                                    const currentMax = product.optionsSchema.properties.quantity.maximum
                                    if (currentMax > maxQuantity) {maxQuantity = currentMax}
                                }
                            })

                            if (minQuantity !== Infinity && maxQuantity !== -Infinity) {
                                if (minQuantity === maxQuantity) {item.quantityRange = `${minQuantity}`} else {item.quantityRange = `${minQuantity} - ${maxQuantity}`}

                                // Находим продукт с минимальной ценой
                                const minPriceProduct = item.products.reduce((minProduct, currentProduct) => {
                                    const currentPrice = parseFloat(currentProduct.price) || Infinity
                                    const minPrice = parseFloat(minProduct.price) || Infinity
                                    return currentPrice < minPrice ? currentProduct : minProduct
                                }, item.products[0])

                                if (minPriceProduct?.price !== undefined) {
                                    const price = parseFloat(minPriceProduct.price)
                                    if (!isNaN(price)) {item.minimumPrice = price;item.pricePerUnit = price}
                                }
                                item.currency = minPriceProduct?.currency ?? item.currency

                                if (minPriceProduct?.optionsSchema?.properties?.billingPeriod?.const !== undefined) {item.billingPeriod = minPriceProduct.optionsSchema.properties.billingPeriod.const}
                                item.availableQuantities = Array.from({length: maxQuantity - minQuantity + 1}, (_, i) => minQuantity + i)
                            }
                        }
                        return item
                    })
                    setProductList(processedData)
                }
            })
            .catch(_ => {})
            .finally(() => {
                const elapsed = Date.now() - loadStartedAt
                const remaining = Math.max(0, MIN_LOADER_MS - elapsed)
                setTimeout(() => setLoader(false), remaining)
            })
    }, [])

    const handleProductSelect = (product) => {
        setSelectedProduct(product)
        let globalMin = Infinity
        let globalMax = -Infinity
        product.products.forEach(prod => {
            if (prod?.optionsSchema?.properties?.quantity?.minimum !== undefined) {
                const quantityMin = prod.optionsSchema.properties.quantity.minimum;if (quantityMin < globalMin) globalMin = quantityMin
                const quantityMax = prod.optionsSchema.properties.quantity.maximum;if (quantityMax > globalMax) globalMax = quantityMax
            }
        })

        setGlobalMinQuantity(globalMin)
        setGlobalMaxQuantity(globalMax)

        // Находим продукт с минимальным количеством лицензий для начального выбора
        const initialProduct = product.products.reduce((minProduct, currentProduct) => {
            const currentQuantity = currentProduct.optionsSchema?.properties?.quantity?.minimum || Infinity
            const minQuantity = minProduct.optionsSchema?.properties?.quantity?.minimum || Infinity
            return currentQuantity < minQuantity ? currentProduct : minProduct
        }, product.products[0])

        setCurrentProduct(initialProduct)
        setSelectedQuantity(initialProduct.optionsSchema?.properties?.quantity?.minimum || 1)
        setCurrentStep(2)
    }

    // Функция для поиска подходящего продукта на основе количества
    const findProductForQuantity = (quantity) => {
        if (!selectedProduct || !selectedProduct.products) return null

        // Ищем продукт, у которого минимальное количество <= выбранному количеству
        const suitableProducts = selectedProduct.products.filter(product => {
            const minQuantity = product.optionsSchema?.properties?.quantity?.minimum
            return minQuantity !== undefined && quantity >= minQuantity
        })

        if (suitableProducts.length === 0) return null

        // Выбираем продукт с наименьшей минимальной границей, подходящей под количество
        return suitableProducts.reduce((bestProduct, currentProduct) => {
            const bestMin = bestProduct.optionsSchema?.properties?.quantity?.minimum || Infinity
            const currentMin = currentProduct.optionsSchema?.properties?.quantity?.minimum || Infinity

            // Предпочитаем продукт с минимальной границей, но не превышающей выбранное количество
            if (currentMin <= quantity && currentMin > bestMin) {
                return currentProduct
            }
            return bestProduct
        }, suitableProducts[0])
    }

    const handleQuantityChange = (e) => {
        const quantity = parseInt(e.target.value) || globalMinQuantity
        if (quantity < globalMinQuantity) {setSelectedQuantity(globalMinQuantity)}
        else if (quantity > globalMaxQuantity) {setSelectedQuantity(globalMaxQuantity)}
        else {setSelectedQuantity(quantity)}
        const suitableProduct = findProductForQuantity(quantity)
        if (suitableProduct) {setCurrentProduct(suitableProduct)}
    }

    const handleQuantityButtonClick = (increment) => {
        setSelectedQuantity(prev => {
            const newQuantity = prev + increment
            if (newQuantity >= globalMinQuantity && newQuantity <= globalMaxQuantity) {

                // Находим подходящий продукт для нового количества ( в апи не приходит диапазон с нужными параметрами )
                const suitableProduct = findProductForQuantity(newQuantity)
                if (suitableProduct) {
                    setCurrentProduct(suitableProduct)
                }
                return newQuantity
            }
            return prev
        })
    }

    const startContinuousChange = (increment) => {
        stopContinuousChange()
        delayRef.current = 300
        const changeQuantity = () => {handleQuantityButtonClick(increment)}
        changeQuantity()
        intervalRef.current = setInterval(changeQuantity, delayRef.current)
        const accelerate = () => {
            clearInterval(intervalRef.current)
            delayRef.current = Math.max(50, delayRef.current * 0.8)
            intervalRef.current = setInterval(changeQuantity, delayRef.current)
        }
        accelerateIntervalRef.current = setInterval(accelerate, 500)
    }

    const stopContinuousChange = () => {
        if (intervalRef.current) {clearInterval(intervalRef.current);intervalRef.current = null}
        if (accelerateIntervalRef.current) {clearInterval(accelerateIntervalRef.current);accelerateIntervalRef.current = null}
    }

    const handleNextStep = () => {
        if (currentStep === 2) {
            if (!profileLoaded) return

            const pricingValidationMessage = getPricingValidationMessage()
            if (pricingValidationMessage) {
                showNotification(pricingValidationMessage, 'error')
                return
            }

            apiMy()
                .then(data => {
                    if (handleAuthErrorAndRedirect({statusCode: data.code, navigate, location})) return
                    if (isSuccessStatus(data.code)) {
                        setUserData(data.data)
                        setEditedUserData({
                            email: data.data.customerOptions?.contacts?.email || data.data.email || '',
                            phone: data.data.customerOptions?.contacts?.phone || data.data.phone || '',
                            name: data.data.customerOptions?.contacts?.name || data.data.name || '',
                            companyName: data.data.customerOptions?.company?.name || data.data.companyName || '',
                            companyPhone: data.data.customerOptions?.company?.phone || '',
                            address: data.data.customerOptions?.company?.address || data.data.address || '',
                            inn: data.data.customerOptions?.company?.code || data.data.inn || '',
                            agreementNumber: data.data.agreementNumber || ''
                        })
                        setCurrentStep(3)
                    }
                })
                .catch(_ => {})
        }
    }

    const handlePreviousStep = () => {if (currentStep === 2) {setCurrentStep(1);setSelectedProduct(null);setCurrentProduct(null)} else if (currentStep === 3) {setCurrentStep(2)}}

    const handleUserDataChange = (field, value) => {
        // Обработка телефона
        if (field === 'phone' || field === 'companyPhone') {
            // Удаляем все нецифровые символы, кроме плюса в начале
            let cleanedValue = value.replace(/[^\d+]/g, '');

            // Если строка пустая или начинается с +, оставляем как есть
            if (cleanedValue === '' || cleanedValue.startsWith('+')) {
                // Удаляем все плюсы кроме первого
                const plusCount = (cleanedValue.match(/\+/g) || []).length;
                if (plusCount > 1) {
                    cleanedValue = '+' + cleanedValue.replace(/\+/g, '');
                }

                // Ограничиваем длину до 13 символов (1 знак + и 12 цифр)
                if (cleanedValue.length > 13) {
                    cleanedValue = cleanedValue.substring(0, 13);
                }
            } else {
                // Если не начинается с +, добавляем его и ограничиваем цифры
                const digitsOnly = cleanedValue.replace(/\D/g, '');
                cleanedValue = '+' + digitsOnly.substring(0, 12);
            }
            value = cleanedValue;
        }
        setEditedUserData(prev => ({...prev, [field]: value}))
    }

    const getPricingValidationMessage = () => {
        if (!isLdap) return ''

        const hasApprovalCode = approvalCode.trim() !== ''
        const cleanedSpecialPrice = specialPrice.trim()
        const hasSpecialPrice = cleanedSpecialPrice !== ''

        if (hasApprovalCode !== hasSpecialPrice) {
            return 'Заполните ApprovalCode и SpecialPrice вместе или оставьте оба поля пустыми'
        }

        if (hasSpecialPrice && !Number.isFinite(Number(cleanedSpecialPrice))) {
            return 'SpecialPrice должен быть числом'
        }

        return ''
    }

    const handleSubscribe = () => {
        if (subscribing) return; // Защита от повторного нажатия

        const pricingValidationMessage = getPricingValidationMessage()
        if (pricingValidationMessage) {
            showNotification(pricingValidationMessage, 'error')
            return
        }

        const companyName = (editedUserData.companyName ?? '').toString().trim()
        const cleanedAgreementNumber = (editedUserData.agreementNumber ?? '').toString().trim()
        const forReq = {
            // Backend resolves the effective product by requested quantity.
            productId: currentProduct.id,
            agreementNumber: cleanedAgreementNumber,
            options: {
                quantity: selectedQuantity
            },
            customer: {
                options: {
                    contacts: {
                        name: (editedUserData.name ?? '').toString().trim(),
                        email: (editedUserData.email ?? '').toString().trim(),
                        phone: (editedUserData.phone ?? '').toString().trim()
                    },
                    company: {
                        name: companyName,
                        code: (editedUserData.inn ?? '').toString().trim(),
                        phone: (editedUserData.companyPhone ?? '').toString().trim(),
                        address: (editedUserData.address ?? '').toString().trim()
                    }
                }
            }
        }

        if (isLdap && approvalCode.trim() !== '' && specialPrice.trim() !== '') {
            forReq.approvalCode = approvalCode.trim()
            forReq.specialPrice = Number(specialPrice.trim())
        }

        if (editedUserData.email === '') {
            showNotification('Не заполнен Email', 'error')
        } else if (!validateEmail(editedUserData.email)) {
            showNotification('Не корректный Email', 'error')
        } else {
            setSubscribing(true); // Блокируем кнопку

            apiSubsAdd(forReq).then(res => {
                if (handleAuthErrorAndRedirect({statusCode: res.code, navigate, location})) return
                if (!isSuccessStatus(res.code)) {
                    showNotification('Ошибка подключения', 'error')
                    setSubscribing(false); // Разблокируем при ошибке
                } else {
                    // Показываем сообщение об успехе
                    showNotification('Подписка успешно подключена! Через 3 секунды вы будете перенаправлены на список подписок', 'success')
                    setSubscriptionSuccess(true)
                    // Кнопка остается заблокированной, так как будет редирект

                    // Переадресация через 5 секунд
                    setTimeout(() => {
                        navigate('/')
                    }, 3000)
                }
            }).catch(error => {
                showNotification('Произошла ошибка при подключении', 'error')
                setSubscribing(false); // Разблокируем при ошибке
            })
        }
    }

    const calculateTotalPrice = () => {if (currentProduct && currentProduct.price) {return (parseFloat(currentProduct.price) * selectedQuantity).toFixed(2);}return '0.00'}

    // полное названия продукта
    const getProductFullName = () => {
        if (!currentProduct) return selectedProduct?.name || ''
        const vendorCode = currentProduct.vendorCode || ''
        const productName = currentProduct.name || ''
        const categoryName = selectedProduct?.name || ''
        return [vendorCode, productName, categoryName].filter(Boolean).join(' - ')
    }

    const translateBillingPeriod = (period) => {
        if (!period) return '-'

        const source = String(period).trim()
        const normalized = source.toLowerCase()
        const dictionary = {
            '1 year': '1 год',
            '2 year': '2 года',
            '3 year': '3 года',
            '1 month': '1 месяц',
            '2 month': '2 месяца',
            '3 month': '3 месяца',
            '1 day': '1 день',
            'day': 'день',
            'days': 'дней',
            'month': 'месяц',
            'months': 'месяцев',
            'year': 'год',
            'years': 'лет'
        }

        return dictionary[normalized] || source
    }

    const renderProductName = (name) => {
        const highlights = ['itHelper', 'Kaspersky', 'PRO32', '1 day', '1 month', '1 year']
        const source = String(name || '')
        const pattern = new RegExp(`(${highlights.map(item => item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g')

        return source.split(pattern).map((part, index) => {
            if (highlights.includes(part)) {
                return <b key={index}>{part}</b>
            }
            return part
        })
    }

    const formatPrice = (price) => {
        const [integer, decimal] = parseFloat(price).toFixed(2).split('.')
        return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + '.' + decimal
    }

    const formatCurrency = (currency) => {
        const normalized = (currency || '').toString().trim().toLowerCase()
        if (!normalized || normalized === 'рубли' || normalized === 'руб' || normalized === 'rur' || normalized === 'rub') {return '₽'}
        if (normalized === 'kzt' || normalized === 'тенге') {return '₸'}
        return (currency || '').toString().trim()
    }

    const [searchTerm, setSearchTerm] = useState('')
    const [searchType, setSearchType] = useState('name')
    const handleSearch = (value) => {setSearchTerm(value)}
    const filteredProductList = productList.filter(item => {
        if (!searchTerm.trim()) return true
        const searchValue = searchTerm.toLowerCase()
        if (searchType === 'name') {
            return item.name.toLowerCase().includes(searchValue)
        } else if (searchType === 'vendorCode') {
            if (item.products && Array.isArray(item.products)) {return item.products.some(product => product.vendorCode && product.vendorCode.toLowerCase().includes(searchValue))}
            return false
        }
        return true
    })

    return <div className="pageSubscriptionAdd">
        <Header/>

        {currentStep === 1 && <BreadCrumbs title={'Подключить подписку → Выбор продукта'}/>}
        {currentStep === 2 && <BreadCrumbs title={'Подключить подписку → Выбор лицензий'}/>}
        {currentStep === 3 && <BreadCrumbs title={'Подключить подписку → Подтверждение'}/>}

        {
            loader ? (
                <div className="pageSubscriptionAddLoader"><Loaders/></div>
            ) : (
                <div className="responsive-table-container">
                    {
                        currentStep === 1 && (<div className="pageSubscriptionAddLoader__filter-form">
                            <div className="filter-item">
                                <input type="text" placeholder="Введите текст для поиска..." className="filter-control search-input"  onChange={(e) => handleSearch(e.target.value)}/>
                            </div>
                            <div className="pageSubscriptionAddLoader__filter-group">
                                <CustomSelect
                                    value={searchType}
                                    options={[
                                        { value: 'name', label: 'по названию продукта' },
                                        { value: 'vendorCode', label: 'по коду поставщика' }
                                    ]}
                                    onChange={(value) => setSearchType(value)}
                                    className="filter-control"
                                    optionLabelKey="label"
                                    optionValueKey="value"
                                />
                            </div>
                        </div>)
                    }
                    {currentStep === 1 && (
                        <table className="responsive-table">
                            <thead>
                            <tr><th>№</th><th>Название</th><th>Лицензий</th><th>Цена от</th><th className="action-header"></th></tr>
                            </thead>
                            <tbody>
                            {filteredProductList.map((item, index) => {
                                const splitSegments = 3
                                const currentFirstWords = item.name.split(' ').slice(0, splitSegments).join(' ');
                                const prevFirstWords = index > 0 ? filteredProductList[index - 1].name.split(' ').slice(0, splitSegments).join(' ') : null;
                                const shouldAddBorder = prevFirstWords && currentFirstWords !== prevFirstWords;
                                return (
                                    <tr key={item.id} className={shouldAddBorder ? 'border-bottom' : ''}>
                                        <td>{index + 1}</td>
                                        <td>{renderProductName(item.name)}</td>
                                        <td>{item.quantityRange}</td>
                                        <td className="price-period-cell">
                                            <div className="price-period-content">
                                                <span>{formatPrice(parseFloat(item.minimumPrice).toFixed(2))} {formatCurrency(item.currency)}</span>
                                                <span>/&nbsp;{translateBillingPeriod(item.billingPeriod)}</span>
                                            </div>
                                        </td>
                                        <td className="action-cell" onClick={() => handleProductSelect(item)}>
                                            <div className="SubsAddTableActionNext">
                                                <div>Выбрать</div>
                                                <div className="iconNext"/>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    )}

                    {currentStep === 2 && selectedProduct && currentProduct && (
                        <div className="responsive-table2">
                            <div>
                                <div><span>Выбранный продукт:</span><span>{getProductFullName()}</span></div>
                                <div>
                                    <span>Лицензий:</span>
                                    <div className="quantity-controls">
                                        <button className="quantity-btn minus" onMouseDown={() => startContinuousChange(-1)} onMouseUp={stopContinuousChange} onMouseLeave={stopContinuousChange} onTouchStart={() => startContinuousChange(-1)} onTouchEnd={stopContinuousChange}>-</button>
                                        <input type="number" value={selectedQuantity} onChange={handleQuantityChange} className="quantity-input" min={globalMinQuantity} max={globalMaxQuantity}/>
                                        <button className="quantity-btn plus" onMouseDown={() => startContinuousChange(1)} onMouseUp={stopContinuousChange} onMouseLeave={stopContinuousChange} onTouchStart={() => startContinuousChange(1)} onTouchEnd={stopContinuousChange}>+</button>
                                    </div>
                                </div>
                                <div><span>Стоимость за единицу:</span><span>{parseFloat(currentProduct.price).toFixed(2)} {formatCurrency(currentProduct.currency || selectedProduct?.currency)}</span></div>
                                <div><span>Итоговая стоимость:</span><span>{calculateTotalPrice()} {formatCurrency(currentProduct.currency || selectedProduct?.currency)}</span></div>
                                <div><span>Период оплаты:</span><span>{translateBillingPeriod(currentProduct.optionsSchema?.properties?.billingPeriod?.const || selectedProduct.billingPeriod)}</span></div>
                                {isLdap && <div><span>ApprovalCode:</span><input className="subscription-pricing-input" type="text" value={approvalCode} onChange={e => setApprovalCode(e.target.value)} /></div>}
                                {isLdap && <div><span>SpecialPrice:</span><input className="subscription-pricing-input" type="number" step="any" value={specialPrice} onChange={e => setSpecialPrice(e.target.value)} /></div>}
                            </div>
                            <div className="allPagesStyleButton-pos">
                                <div className="allPagesStyleButton-group">
                                    <div className="allPagesStyleButton" onClick={handlePreviousStep}>← Предыдущий шаг</div>
                                    <div className={`allPagesStyleButton underlined ${!profileLoaded ? 'disabled' : ''}`} onClick={profileLoaded ? handleNextStep : undefined}>Далее →</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {currentStep === 3 && userData && currentProduct && (
                        <div className="responsive-table3">
                            <div className="form-section">
                                <div><span>Продукт:</span><span className="value">{getProductFullName()}</span></div>
                                <div><span>Лицензий:</span><span className="value">{selectedQuantity}</span></div>
                                <div><span>Итоговая стоимость:</span><span className="value">{calculateTotalPrice()} {formatCurrency(currentProduct.currency || selectedProduct?.currency)}</span></div>
                                <div><span>Период оплаты:</span><span className="value">{translateBillingPeriod(currentProduct.optionsSchema?.properties?.billingPeriod?.const || selectedProduct.billingPeriod)}</span></div>
                                <div className="subscription-form-block">
                                    <h3>Данные о компании</h3>
                                    <div><span>Название компании:</span><input type="text" value={editedUserData.companyName || ''} onChange={(e) => handleUserDataChange('companyName', e.target.value)}/></div>
                                    <div><span>ИНН (УНП/БИН):</span><input type="text" value={editedUserData.inn} onChange={(e) => handleUserDataChange('inn', e.target.value)}/></div>
                                    <div><span>Адрес:</span><input type="text" value={editedUserData.address} onChange={(e) => handleUserDataChange('address', e.target.value)}/></div>
                                    <div><span>Телефон компании:</span><input type="text" maxLength={13} value={editedUserData.companyPhone || ''} onChange={(e) => handleUserDataChange('companyPhone', e.target.value)}/></div>
                                    <div><span>№ договора:</span><input type="text" value={editedUserData.agreementNumber} onChange={(e) => handleUserDataChange('agreementNumber', e.target.value)}/></div>
                                </div>
                                <div className="subscription-form-block">
                                    <h3>Данные о клиенте</h3>
                                    <div><span>ФИО:</span><input type="text" value={editedUserData.name} onChange={(e) => handleUserDataChange('name', e.target.value)}/></div>
                                    <div><span>Email*:</span><input type="text" value={editedUserData.email} onChange={(e) => handleUserDataChange('email', e.target.value)}/></div>
                                    <div><span>Телефон:</span><input type="text" maxLength={13} value={editedUserData.phone} onChange={(e) => handleUserDataChange('phone', e.target.value)}/></div>
                                </div>
                            </div>
                            <div className="allPagesStyleButton-pos">
                                <div className="allPagesStyleButton-group">
                                    <div className="allPagesStyleButton" onClick={handlePreviousStep}>← Предыдущий шаг</div>
                                    <div className={`allPagesStyleButton underlined ${subscribing ? 'disabled' : ''}`} onClick={!subscribing ? handleSubscribe : undefined}>
                                        {subscribing ? 'Подключение...' : 'Подключить →'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Уведомления */}
                    {notification && (<Notification message={notification.message} type={notification.type} onClose={hideNotification} duration={3000}/>)}
                </div>
            )
        }
        <br/>
        <br/>
        <br/>
        <Footer/>
    </div>
}

export default SubscriptionAdd
